import {TimeChunk, updateTimeToInteractive} from './timeToInteractive';
import {
  MIN_NETWORK_ELAPSED_TIME_MILLIS,
  NETWORK_EVENT_CONTEXT,
  updatePageLongTask,
  reportTimedEvent,
  logToDataDog,
} from './report';
import {assertUnreachable, ValueOf} from '../types';

/*
This file is responsible for reading data from the browser's built in PerformanceObserver and translating it into
events for our profiler.
*/

// Requests to these domains will count towards the time-to-interactive metric.
const TTI_DOMAIN_INCLUSIONS = [
  'wandb.com',
  'wandb.ai',
  'wandb.us',
  'wandb.test',
  'googleapis.com',
];

const TTI_WANDB_ANALYTICS_DOMAIN = 'analytics.wandb.ai';

// Requests to these domains will be ignored in the time-to-interactive metric.
const TTI_DOMAIN_EXCLUSIONS = [
  '6sc.co',
  'ads-twitter.com',
  'bing.com',
  'datadoghq.com',
  'browser-intake-datadoghq.com',
  'clarity.ms',
  'cookielaw.org',
  'doubleclick.net',
  'fullstory.com',
  'getbeamer.com',
  'github.com',
  'google-analytics.com',
  'google.com',
  'google.com',
  'googleoptimize.com',
  'googletagmanager.com',
  'gravatar.com',
  'intercom.io',
  'intercomcdn.com',
  'jquery.com',
  'jscloud.net',
  'licdn.com',
  'linkedin.com',
  'onetrust.com',
  'pendo.io',
  'reddit.com',
  'redditstatic.com',
  'segment.io',
  'sentry.io',
  't.co',
  'twitter.com',
  'wordlift.io',
  'wp.com',
  'zdassets.com',
  'zendesk.com',
  'zoominfo.com',
  'zopim.com',
];

const FRIENDLY_ENTRY_TYPE_NAMES = {
  // A long task is any uninterrupted period where the main UI thread is busy for 50ms or longer
  // note that longtask and event/first-input will overlap and report the same issue multiple times
  longtask: 'UI thread busy',
  // resource performance event occurs when a network request takes a long time to fulfill
  resource: 'Network',
  // event and first-input both measure how long it takes for the app to respond to input from the user
  // the value of 'duration' is the time from startTime to the next rendering paint.
  event: 'Slow UI response time',
  'first-input': 'First input delay',
} as const;

let lastSlowResponseTime = 0;

if (window.PerformanceObserver) {
  const observer = new PerformanceObserver(list => {
    list.getEntries().forEach(entry => {
      switch (entry.entryType) {
        case 'resource':
          const url = entry.name;
          const domain = new URL(url).hostname.toLowerCase();
          const domainIsExcluded = TTI_DOMAIN_EXCLUSIONS.some(excludedDomain =>
            domain.includes(excludedDomain)
          );
          const domainIsIncluded = TTI_DOMAIN_INCLUSIONS.some(
            includedDomain =>
              TTI_WANDB_ANALYTICS_DOMAIN !== includedDomain &&
              domain.includes(includedDomain)
          );
          // We've found that trying to get all domains in the exclude list is too tricky,
          // so for now going with an include list and logging the unknowns.
          // We may switch back to exclude list in the future when we have a stable TTI and
          // are confident that we know what domains to exclude/include.
          if (domainIsIncluded) {
            considerForUpdateTTI(getTimeChunk(entry), TimeChunkTypes.NETWORK);
          }
          if (entry.duration < MIN_NETWORK_ELAPSED_TIME_MILLIS) {
            return;
          }
          // GraphQL queries are logged using reportQueryPerfEvent
          if (url.endsWith('/graphql') || url.endsWith('/graphql2')) {
            return;
          }
          reportPerfObserverEvent(entry, {
            ...NETWORK_EVENT_CONTEXT,
            url,
          });
          if (!domainIsExcluded && !domainIsIncluded) {
            // Flag unknown domains, so we can know to add them to TTI_DOMAIN_INCLUSIONS or TTI_DOMAIN_EXCLUSIONS
            logToDataDog(
              'PerformanceObserver resource had unknown domain: ' + domain,
              {unknownDomain: domain}
            );
          }
          break;
        case 'longtask':
          considerForUpdateTTI(getTimeChunk(entry), TimeChunkTypes.LONGTASK);
          updatePageLongTask(entry.duration);
          reportPerfObserverEvent(entry);
          break;
        case 'event':
          // we get a lot of repeated data for this entry type,
          // they come in order so just ignore the last one.
          if (lastSlowResponseTime !== entry.startTime) {
            lastSlowResponseTime = entry.startTime;
            reportPerfObserverEvent(entry);
          }
          break;

        case 'first-input':
          reportPerfObserverEvent(entry);
          break;
      }
    });
  });
  observer.observe({
    entryTypes: ['resource', 'event', 'first-input', 'longtask'],
  });
}

const reportPerfObserverEvent = (
  entry: PerformanceEntry,
  context: Record<string, any> = {}
) => {
  const perfObserverEventName =
    entry.entryType in FRIENDLY_ENTRY_TYPE_NAMES
      ? FRIENDLY_ENTRY_TYPE_NAMES[
          entry.entryType as keyof typeof FRIENDLY_ENTRY_TYPE_NAMES
        ]
      : entry.entryType;

  reportTimedEvent(
    // duration is in millis. We use floor because more precision isn't useful
    Math.floor(entry.duration),
    Math.floor(entry.startTime), // note that this will not use the same value for 0 as perfStartTimeMs
    'PerfObserver: ' + perfObserverEventName,
    context
  );
};

export const getTimeChunk = (entry: PerformanceEntry): TimeChunk => ({
  // we round these since for TTI calculations we don't need sub-millisecond
  start: Math.round(entry.startTime),
  end: Math.round(entry.startTime + entry.duration),
});

export const TimeChunkTypes = {
  NETWORK: 'network',
  LONGTASK: 'longtask',
} as const;
export type TimeChunkType = ValueOf<typeof TimeChunkTypes>;

/*
How did we pick these limits?
* longtasks: we're finding that when the user is hovering over charts, it is getting
  marked as long tasks, but just above the 50ms threshold for longtasks, so we are just
  bumping up that threshold a bit.
* network: this is somewhat arbitrary, so it may need adjusting. The idea is that 
  500ms is still "fast" from user's viewpoint for loading data. Hopefully we can 
  make this value lower over time. This was picked by looking at network request in
  workspaces, and see what duration looked like for those call, and aiming for a 
  ~p90 value.
*/
export const MINIMUM_NETWORK_DURATION_MS = 500;
export const MINIMUM_LONGTASK_DURATION_MS = 200;

/*
In watching what's happening during real page loads, we've found that due to the UI
of workspaces and reports, there tend to be many small network requests and longtasks
occurring *after* the page appears loaded to the user. This makes TTI inaccurate for
measuring page load as perceived by users. 

So we are ignoring events that are very quick. Ultimately, we are trying to track whether the app feels
responsive, so this seems like a reasonable tradeoff. This is an extension of what is happening
with longtasks, where the browser won't report to us anything less than 50ms. 

It has the downside that during page load we sometimes have a cascade of requests happening, 
and a series of quick events would be missed. For example, if a page requires 20 400ms 
requests made in series, this logic will miss that and the TTI reported will be lower than
it should be.

Fundamentally, if the UI relies on a series of network requests, those round trips
are very inefficient - the backend should be returning all the data needed for a particular view
so no roundtripping is needed. This is aspirational since that's not the current case. So longer
term, this logic for TTI should work well.

For now, we are just trying to find users encountering truly bad load times, and using higher
bars for reporting a longtask or network request should filter out a bunch of noise.
*/
export const considerForUpdateTTI = (tc: TimeChunk, type: TimeChunkType) => {
  const duration = tc.end - tc.start;
  switch (type) {
    case TimeChunkTypes.LONGTASK:
      if (duration >= MINIMUM_LONGTASK_DURATION_MS) {
        updateTimeToInteractive(tc);
      }
      break;

    case TimeChunkTypes.NETWORK:
      if (duration >= MINIMUM_NETWORK_DURATION_MS) {
        updateTimeToInteractive(tc);
      }
      break;
    default:
      assertUnreachable(type);
  }
};
